import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';

const COOKIE_CONSENT_KEY = 'guardiansfit_cookie_consent';
const DISCLAIMER_KEY = 'gf_disclaimer_v1_accepted';

const CookieConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent) return;

    // Wait until the legal disclaimer has been accepted; otherwise the
    // cookie banner would overlap and trap the user (especially on mobile).
    const showIfDisclaimerAccepted = () => {
      if (localStorage.getItem(DISCLAIMER_KEY)) {
        setShowBanner(true);
        return true;
      }
      return false;
    };

    // Si ya está aceptado, mostrar tras un breve delay (UX igual que antes)
    if (showIfDisclaimerAccepted()) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }

    // Si no, hacer polling cada 500ms hasta que el usuario acepte el disclaimer
    const poll = setInterval(() => {
      if (showIfDisclaimerAccepted()) {
        clearInterval(poll);
      }
    }, 500);
    return () => clearInterval(poll);
  }, []);

  const saveConsent = (analytics) => {
    const consentData = {
      necessary: true, // Always true
      analytics: analytics,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleRejectAll = () => {
    saveConsent(false);
  };

  const handleSavePreferences = () => {
    saveConsent(analyticsConsent);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] animate-in slide-in-from-bottom duration-500"
      data-testid="cookie-consent-banner"
    >
      <div className="bg-zinc-900/95 backdrop-blur-lg border-t border-zinc-700/50 shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 py-4">
          {!showSettings ? (
            // Main Banner View
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-white text-sm mb-1">
                    Usamos cookies
                  </h3>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Utilizamos cookies técnicas necesarias para el funcionamiento del sitio y, con tu consentimiento, 
                    cookies analíticas para mejorar nuestros servicios. Puedes configurar tus preferencias o aceptar 
                    todas las cookies. Más información en nuestra{' '}
                    <Link to="/legal/cookies" className="text-primary hover:underline">
                      Política de Cookies
                    </Link>.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2 w-full md:w-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="text-zinc-400 hover:text-white text-xs"
                  data-testid="cookie-settings-btn"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Configurar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-xs"
                  data-testid="cookie-reject-btn"
                >
                  Solo necesarias
                </Button>
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
                  data-testid="cookie-accept-btn"
                >
                  Aceptar todas
                </Button>
              </div>
            </div>
          ) : (
            // Settings View
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Configurar Cookies
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSettings(false)}
                  className="text-zinc-400 hover:text-white h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {/* Necessary Cookies - Always On */}
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Cookies Técnicas (Necesarias)</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Esenciales para el funcionamiento del sitio. No se pueden desactivar.
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-green-900/30 text-green-400 text-xs rounded-full">
                    Siempre activas
                  </div>
                </div>

                {/* Analytics Cookies - Toggleable */}
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="font-medium text-white text-sm">Cookies Analíticas (Opcionales)</p>
                    <p className="text-xs text-zinc-500 mt-1">
                      Nos ayudan a entender cómo usas la plataforma para mejorarla.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsConsent}
                      onChange={(e) => setAnalyticsConsent(e.target.checked)}
                      className="sr-only peer"
                      data-testid="cookie-analytics-toggle"
                    />
                    <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRejectAll}
                  className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                  data-testid="cookie-reject-all-btn"
                >
                  Rechazar opcionales
                </Button>
                <Button
                  size="sm"
                  onClick={handleSavePreferences}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="cookie-save-preferences-btn"
                >
                  Guardar preferencias
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
